import { useMutation } from '@apollo/client';
import { CircleMinusIcon } from 'lucide-react';
import React from 'react';

import { cacheUtils } from '@eluve/apollo-client';
import { TooltipLabel } from '@eluve/blocks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

const deleteSiteBannerMutation = graphql(`
  mutation deleteSiteBanner($siteBannerId: uuid!) {
    deleteSiteBannersByPk(id: $siteBannerId) {
      id
    }
  }
`);

export interface DeleteSiteBannerActionProps {
  siteBannerId: string;
}

export const DeleteSiteBannerAction: React.FC<DeleteSiteBannerActionProps> = ({
  siteBannerId,
}) => {
  const toast = useToast();
  const [deleteSiteBanner] = useMutation(deleteSiteBannerMutation, {
    onError: () => toast.error('Failed to delete site banner'),
    optimisticResponse: () => ({
      deleteSiteBannersByPk: {
        __typename: 'SiteBanners' as const,
        id: siteBannerId,
      },
    }),
    update: (cache) => {
      cacheUtils.evict({
        typeName: 'SiteBanners',
        key: { id: siteBannerId },
      });
      cache.gc();
    },
  });

  const handleDelete = () => deleteSiteBanner({ variables: { siteBannerId } });

  return (
    <AlertDialog>
      <TooltipLabel label="Delete site banner">
        <AlertDialogTrigger asChild>
          <CircleMinusIcon className="cursor-pointer text-bg-negative-strong-primary" />
        </AlertDialogTrigger>
      </TooltipLabel>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-text-tertiary">
            Are you sure?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This will delete site banner permanently.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus className="text-text-tertiary">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-bg-negative-strong-primary text-text-negative-strong hover:bg-bg-negative-strong-primary/90"
            onClick={handleDelete}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
